/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */
import React, { useState, useEffect } from 'react';
import { Users, ChevronDown, ChevronRight, Inbox, Activity, Mail, Phone, TrendingUp } from 'react-feather';
import AuthService from '../../services/authService';
import '../admin/AdminDashboard.css';
import './AgentDashboard.css';

const AgentTeamRoster = ({ agentInfo }) => {
  const [teamRoster, setTeamRoster] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedMembers, setExpandedMembers] = useState({});

  const API_URL = 'https://back-end-x7tx.onrender.com/api';

  useEffect(() => {
    fetchTeamRoster();
  }, [agentInfo]);

  const fetchTeamRoster = async () => {
    if (!agentInfo || !agentInfo._id) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/agents/${agentInfo._id}/team-roster`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTeamRoster(data.data || []);
      } else {
        setError(data.error || 'Failed to load team roster');
      }
    } catch (err) {
      console.error('Error fetching team roster:', err);
      setError('Error loading team roster');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMemberExpanded = (memberId) => {
    setExpandedMembers(prev => ({
      ...prev,
      [memberId]: !prev[memberId]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    // Remove all non-digits
    const cleaned = phone.replace(/\D/g, '');
    // Check if it's a 10-digit number
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    // Check if it's 11 digits starting with 1
    if (cleaned.length === 11 && cleaned[0] === '1') {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    // Return original if doesn't match expected format
    return phone;
  };

  const formatLastActive = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return formatDate(dateString);
  };

  // Calculate team totals
  const teamTotals = {
    totalMembers: teamRoster.length,
    totalPoints: teamRoster.reduce((sum, m) => sum + (m.pointsBalance || 0), 0),
    totalClaimedUsers: teamRoster.reduce((sum, m) => sum + (m.claimedUsersCount || 0), 0),
    totalSwipes: teamRoster.reduce((sum, m) => 
      m.claimedUsers?.reduce((s, u) => s + (u.activity?.totalSwipes || 0), 0) || 0, 0
    )
  };

  const content = (
    <div style={{ padding: '40px', position: 'relative', zIndex: 10 }}>
      <style>{`
        .light-mode .team-stat-card {
          background: #f9f8f5 !important;
          border: 1px solid rgba(26, 26, 26, 0.1) !important;
        }
        .light-mode .team-stat-card .stat-value {
          color: #1a1a1a !important;
        }
        .light-mode .team-stat-card .stat-label {
          color: rgba(26, 26, 26, 0.6) !important;
        }
        .light-mode .team-member-card {
          background: #f9f8f5 !important;
          border: 1px solid rgba(26, 26, 26, 0.12) !important;
        }
        .light-mode .team-member-card:hover {
          background: #f5f4f0 !important;
        }
        .light-mode .team-member-header:hover {
          background: rgba(26, 26, 26, 0.03) !important;
        }
        .light-mode .team-member-expanded {
          background: rgba(26, 26, 26, 0.04) !important;
          border-top: 1px solid rgba(26, 26, 26, 0.1) !important;
        }
        .light-mode .claimed-user-card {
          background: #ffffff !important;
          border: 1px solid rgba(26, 26, 26, 0.1) !important;
        }
        .light-mode .team-empty-state {
          background: #f9f8f5 !important;
          border: 1px solid rgba(26, 26, 26, 0.1) !important;
        }
      `}</style>
      
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          color: '#ef4444'
        }}>
          {error}
        </div>
      )}

      {/* Team Summary Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div className="team-stat-card" style={{
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div className="stat-value" style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '4px' }}>
            {teamTotals.totalMembers}
          </div>
          <div className="stat-label" style={{ fontSize: '0.875rem', opacity: 0.7 }}>Team Members</div>
        </div>
        <div className="team-stat-card" style={{
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div className="stat-value" style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '4px' }}>
            {teamTotals.totalPoints}
          </div>
          <div className="stat-label" style={{ fontSize: '0.875rem', opacity: 0.7 }}>Total Points</div>
        </div>
        <div className="team-stat-card" style={{
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div className="stat-value" style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '4px' }}>
            {teamTotals.totalClaimedUsers}
          </div>
          <div className="stat-label" style={{ fontSize: '0.875rem', opacity: 0.7 }}>Claimed Users</div>
        </div>
        <div className="team-stat-card" style={{
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div className="stat-value" style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '4px' }}>
            {teamTotals.totalSwipes}
          </div>
          <div className="stat-label" style={{ fontSize: '0.875rem', opacity: 0.7 }}>Total Swipes</div>
        </div>
      </div>

      {/* Team Members List */}
      {isLoading ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px',
          opacity: 0.7
        }}>
          Loading team roster...
        </div>
      ) : teamRoster.length === 0 ? (
        <div className="team-empty-state" style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '16px',
          padding: '60px',
          textAlign: 'center',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Users size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
          <p style={{ fontSize: '1.1rem', opacity: 0.7, marginBottom: '8px' }}>No team members yet</p>
          <p style={{ fontSize: '0.9rem', opacity: 0.5 }}>
            Contact your administrator to add agents to your team
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {teamRoster.map(member => (
            <div
              key={member._id}
              className="team-member-card"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                overflow: 'hidden',
                transition: 'all 0.2s ease'
              }}
            >
              {/* Member Header - Always Visible */}
              <div
                className="team-member-header"
                onClick={() => toggleMemberExpanded(member._id)}
                style={{
                  padding: '20px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {/* Avatar */}
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: member.profileImage && member.profileImage !== 'default-profile.jpg' 
                      ? 'transparent' 
                      : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    border: '3px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    {member.profileImage && member.profileImage !== 'default-profile.jpg' ? (
                      <img
                        src={member.profileImage}
                        alt={member.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <span style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                        {member.name?.charAt(0).toUpperCase() || '?'}
                      </span>
                    )}
                  </div>

                  {/* Name & Info */}
                  <div style={{ textAlign: 'left' }}>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '4px', textAlign: 'left' }}>
                      {member.name}
                    </h3>
                    <p style={{ fontSize: '0.85rem', opacity: 0.6, marginBottom: '2px', textAlign: 'left' }}>
                      {member.phone ? formatPhoneNumber(member.phone) : 'No phone'}
                    </p>
                    <p style={{ fontSize: '0.85rem', opacity: 0.6, textAlign: 'left' }}>
                      {member.email}
                    </p>
                  </div>
                </div>

                {/* Stats Preview */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>
                      {member.pointsBalance || 0}
                    </div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>Points</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>
                      {member.claimedUsersCount || 0}
                    </div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>Users</div>
                  </div>
                  <div style={{
                    transition: 'transform 0.2s',
                    transform: expandedMembers[member._id] ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}>
                    <ChevronDown size={24} style={{ opacity: 0.5 }} />
                  </div>
                </div>
              </div>

              {/* Expanded Section - Claimed Users */}
              {expandedMembers[member._id] && (
                <div className="team-member-expanded" style={{
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  padding: '20px 24px',
                  background: 'rgba(0, 0, 0, 0.2)'
                }}>
                  {member.claimedUsers && member.claimedUsers.length > 0 ? (
                    <>
                      <h4 style={{ 
                        fontSize: '0.9rem', 
                        fontWeight: '600', 
                        marginBottom: '16px',
                        opacity: 0.8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Activity size={16} />
                        Claimed Users ({member.claimedUsers.length})
                      </h4>
                      <div style={{
                        display: 'grid',
                        gap: '12px'
                      }}>
                        {member.claimedUsers.map(user => (
                          <div
                            key={user._id}
                            className="claimed-user-card"
                            style={{
                              background: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '12px',
                              padding: '16px',
                              display: 'grid',
                              gridTemplateColumns: '1fr auto',
                              gap: '16px',
                              alignItems: 'center'
                            }}
                          >
                            <div>
                              <div style={{ 
                                fontWeight: '500', 
                                marginBottom: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                {user.name}
                                <span style={{
                                  fontSize: '0.7rem',
                                  padding: '2px 8px',
                                  borderRadius: '20px',
                                  background: user.leadStatus === 'converted' 
                                    ? 'rgba(34, 197, 94, 0.2)' 
                                    : user.leadStatus === 'contacted'
                                    ? 'rgba(59, 130, 246, 0.2)'
                                    : 'rgba(255, 255, 255, 0.1)',
                                  color: user.leadStatus === 'converted'
                                    ? '#22c55e'
                                    : user.leadStatus === 'contacted'
                                    ? '#3b82f6'
                                    : 'inherit',
                                  textTransform: 'capitalize'
                                }}>
                                  {user.leadStatus || 'claimed'}
                                </span>
                              </div>
                              <div style={{ 
                                fontSize: '0.8rem', 
                                opacity: 0.6,
                                display: 'flex',
                                gap: '16px',
                                flexWrap: 'wrap'
                              }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <Mail size={12} /> {user.email}
                                </span>
                                {user.phone && (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Phone size={12} /> {formatPhoneNumber(user.phone)}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* User Activity Stats */}
                            <div style={{
                              display: 'flex',
                              gap: '20px',
                              alignItems: 'center'
                            }}>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ 
                                  fontSize: '1rem', 
                                  fontWeight: '600',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  justifyContent: 'center'
                                }}>
                                  <TrendingUp size={14} style={{ opacity: 0.7 }} />
                                  {user.activity?.totalSwipes || 0}
                                </div>
                                <div style={{ fontSize: '0.7rem', opacity: 0.5 }}>Swipes</div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ 
                                  fontSize: '1rem', 
                                  fontWeight: '600',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  justifyContent: 'center'
                                }}>
                                  <Inbox size={14} style={{ opacity: 0.7 }} />
                                  {user.matchesSent || 0}
                                </div>
                                <div style={{ fontSize: '0.7rem', opacity: 0.5 }}>Matches Sent</div>
                              </div>
                              <div style={{ textAlign: 'center', minWidth: '80px' }}>
                                <div style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                                  {formatLastActive(user.activity?.lastActive)}
                                </div>
                                <div style={{ fontSize: '0.7rem', opacity: 0.5 }}>Last Active</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '24px',
                      opacity: 0.5
                    }}>
                      No claimed users yet
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return content;
};

export default AgentTeamRoster;